import { computed, reactive, readonly } from 'vue'
import { EnumsDomStorage, FrontendErrorCode, MetadataKey } from '@/types/enums'
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import appStore from '@/stores/app.store'
import accountStore from '@/stores/account.store'
import apiService from '@/services/api.service'
import ApiError from '@/types/ApiError'
import GoogleTagManagerService from '@/services/google-tag-manager.service'
import unitStore from '@/stores/unit.store'

interface accountStoreStates {
  token: string | null
  useDirectAuth0Token: boolean // Track if backend supports direct Auth0 tokens
  isTestingAuth: boolean // Flag to prevent interceptor redirect during auth test
}

// Check if we've already determined backend auth method
const savedAuthMethod = localStorage.getItem('useDirectAuth0Token')
const state = reactive({
  token: null, // Don't load from localStorage - will be set during auth flow
  useDirectAuth0Token:
    savedAuthMethod === null ? true : savedAuthMethod === 'true', // Try new approach first, or use saved preference
  isTestingAuth: false,
} as accountStoreStates)

const checkRouteAuth = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  /**
   * Clear PII data from calendly links
   * @see https://careerpartner.atlassian.net/browse/MPAD-1157
   */
  if (to.query && to.query['assigned_to']) {
    return next({ query: {} })
  }

  if (to.path === '/maintenance') {
    return next()
  }

  // Check Auth Redirect and tokens
  if (to.hash && !['#motivation'].includes(to.hash)) {
    // is redirect from Auth0

    const hash = to.hash.replace('#access_token', '?access_token')
    const urlParams = new URLSearchParams(hash)

    const accessToken = urlParams.get('access_token') || ''
    const idToken = urlParams.get('id_token') || ''

    // load last url path | MPAD-643
    const lastUrl = sessionStorage.getItem(EnumsDomStorage.ORIGIN_URL_PATH)

    // B2B always uses Auth0 token directly (MPAD-1174)
    if (appStore.state.isB2b) {
      state.token = accessToken
      state.useDirectAuth0Token = true
      return next(lastUrl ?? to.path)
    }

    // B2C: Try new approach (direct Auth0 token) first, fallback to legacy /auth endpoint
    if (state.useDirectAuth0Token) {
      // Try using Auth0 token directly (OCPT-640 - new backend)
      state.token = accessToken
      state.isTestingAuth = true // Prevent interceptor from redirecting during test

      // Test if backend supports direct Auth0 tokens by calling /account
      try {
        await apiService.account.get()
        // Success - backend supports direct Auth0 tokens
        console.log('[Auth] Backend supports direct Auth0 tokens')
        state.isTestingAuth = false
        localStorage.setItem('useDirectAuth0Token', 'true')
        return next(lastUrl ?? to.path)
      } catch (error: any) {
        state.isTestingAuth = false
        // Check if it's an auth error (backend doesn't support direct Auth0 tokens)
        const backendErrorCode = error?.code
        if ([1000, 1003, 1004].includes(backendErrorCode)) {
          console.log(
            '[Auth Fallback] Backend does not support direct Auth0 tokens, falling back to /auth endpoint',
          )
          state.useDirectAuth0Token = false
          localStorage.setItem('useDirectAuth0Token', 'false')

          // Fallback to legacy /auth endpoint (old backend)
          await apiService.auth.post(accessToken, idToken).then(
            (result) => {
              state.token = result.data.api_token
              localStorage.setItem(
                EnumsDomStorage.AUTH_TOKEN,
                state.token || '',
              )
              return next(lastUrl ?? to.path)
            },
            async (authError: ApiError) => {
              console.log('[B2C] /auth check error', authError)
              if (!appStore.state.apiError) {
                await appStore.setApiError(
                  authError.code ?? FrontendErrorCode.AUTH_ERROR,
                  authError.message,
                )
              }
              return next(lastUrl ?? to.path)
            },
          )
        } else {
          // Different error, not auth-related
          return next(lastUrl ?? to.path)
        }
      }
    } else {
      // Already know we need legacy auth - but /auth endpoint might be removed
      try {
        const result = await apiService.auth.post(accessToken, idToken)
        state.token = result.data.api_token
        localStorage.setItem(EnumsDomStorage.AUTH_TOKEN, state.token || '')
        return next(lastUrl ?? to.path)
      } catch (error: any) {
        console.log('[B2C] /auth endpoint error', error)

        // Check if /auth endpoint no longer exists (404) - backend upgraded
        if (error.code === 404 || error.message?.includes('404')) {
          console.log(
            '[Auth Upgrade] /auth endpoint not found, switching to direct Auth0 tokens',
          )
          state.useDirectAuth0Token = true
          localStorage.setItem('useDirectAuth0Token', 'true')

          // Use Auth0 token directly
          state.token = accessToken
          return next(lastUrl ?? to.path)
        }

        // Other error with /auth endpoint
        if (!appStore.state.apiError) {
          await appStore.setApiError(
            error.code ?? FrontendErrorCode.AUTH_ERROR,
            error.message,
          )
        }
        return next(lastUrl ?? to.path)
      }
    }
  }

  //## Route call to route without authorization needed (like privacy policy) -> No Token needed
  if (to.meta.allowUnauthorized) {
    appStore.setDisableNavbar(true)
    appStore.toggleInitialLoading()
    return next()
  }

  /** validation of token through apiService **/
  if (!state.token && !appStore.state.apiError) {
    // Clear any old tokens from localStorage before redirecting to login
    localStorage.removeItem(EnumsDomStorage.AUTH_TOKEN)

    // load last url path | MPAD-643
    sessionStorage.setItem(EnumsDomStorage.ORIGIN_URL_PATH, to.fullPath)
    console.log('[REDIRECT] set lastUrl', to.fullPath)
    sessionStorage.setItem(
      EnumsDomStorage.ORIGIN_URL_QUERIES,
      JSON.stringify({ time: new Date(), queries: to.query }),
    )
    await appStore.redirectToLogin()
    return false
  }

  // Don't redirect to onboarding if user is viewing document preview
  if (to.name === 'DocumentsPreview') {
    return next()
  }

  if (
    !appStore.state.initialLoading &&
    !onboardingDone.value &&
    to.meta.onboarding &&
    to.name !== to.meta.onboarding
  ) {
    return next({ name: 'ds.home' })
  }

  // disable navbar if in route meta
  appStore.setDisableNavbar(!!to.meta.noNav)

  return next()
}

const onboardingDone = computed(() => {
  const onboardingIsDone = accountStore.getMetadataValue(
    MetadataKey.ONBOARDING_DONE,
  )
  const interviewIsDone = unitStore.state?.unit?.participateAdvisorInterviewOn

  return onboardingIsDone || interviewIsDone
})

const logoutUser = () => {
  GoogleTagManagerService.trackEvent('LogoutClick', 'logout')
  // TODO: Logout in backend
  const savedAuthMethod = localStorage.getItem('useDirectAuth0Token') // Save before clear
  localStorage.clear()

  // Restore auth method preference - don't reset on logout
  if (savedAuthMethod !== null) {
    localStorage.setItem('useDirectAuth0Token', savedAuthMethod)
  }

  const logoutUrl = appStore.state.isB2b
    ? `${import.meta.env.VITE_AUTH0_LOGOUT_URL}${appStore.state.baseUrl}`
    : `${import.meta.env.VITE_AUTH0_B2C_LOGOUT_URL}${appStore.state.baseUrl}`
  console.log('logoutUser with redirect', logoutUrl)
  window.location.replace(logoutUrl)
}

// Export complete store at once
export default {
  state: readonly(state),
  checkRouteAuth,
  logoutUser,
  onboardingDone,
}
